import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  getToken,
  setToken,
  clearToken,
  setOnUnauthorized,
  authWithGoogle,
  exchangeCode,
} from './api';

interface User {
  email: string;
  name: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (idToken: string) => Promise<void>;
  loginWithCode: (code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

/** Decode JWT payload without verification (client-side display only) */
function decodePayload(token: string): User | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return { email: payload.email, name: payload.name };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  // Check existing token on mount
  useEffect(() => {
    const token = getToken();
    if (token) {
      const decoded = decodePayload(token);
      if (decoded) {
        setUser(decoded);
      } else {
        clearToken();
      }
    }
    setLoading(false);

    // Register 401 handler
    setOnUnauthorized(logout);
  }, [logout]);

  const login = useCallback(async (idToken: string) => {
    setLoading(true);
    try {
      const res = await authWithGoogle(idToken);
      setToken(res.token);
      setUser({ email: res.email, name: res.name });
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithCode = useCallback(async (code: string) => {
    setLoading(true);
    try {
      const res = await exchangeCode(code);
      setToken(res.token);
      setUser({ email: res.email, name: res.name });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
